import React, { useMemo } from "react";
import "./HomePage.css";

/* ============================================================
   src/HomePage.tsx
   ============================================================
   The landing screen. Opening the app used to drop you straight
   onto a blank Deck Estimate with no sense of where you were or
   what you had been working on — every session started as if it
   were your first. Home answers three questions before you touch
   anything: what were you in the middle of, what have you worked
   on lately, and where else can you go.

   Deliberately built on data that is already in memory — the
   local recents list and the estimate currently loaded — so it
   paints instantly and works with no network. Nothing here waits
   on Supabase; a home screen that spins is worse than no home
   screen at all.
   ============================================================ */

export type HomeRecentFile = { name: string; json: any; ts: number };

export type HomeNavTarget =
  | "estimator"
  | "proposals"
  | "contract"
  | "visualLibrary"
  | "pricingAdmin"
  | "analytics"
  | "settings";

export type HomePageProps = {
  /** Sender name from Settings; the greeting falls back to a plain hello. */
  userName?: string;
  /** Org name shown under the greeting, when one is loaded. */
  orgName?: string;
  /** Local recents (most recent first) — the same list the File menu uses. */
  recents: HomeRecentFile[];
  /** Client on the estimate currently in memory, for the Continue card. */
  currentClientName?: string;
  currentTown?: string;
  /** True when the loaded estimate has real scope on it worth returning to. */
  hasWorkInProgress: boolean;
  onResume: () => void;
  onNewEstimate: () => void;
  onOpenFile: () => void;
  onOpenRecent: (rf: HomeRecentFile) => void;
  onNavigate: (target: HomeNavTarget) => void;
  canEditPricing: boolean;
};

function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/** "just now" / "20m ago" / "3h ago" / "Yesterday" / "4 days ago" / "Sep 2".
 *  Past a week the absolute date is more useful than a growing day count. */
export function relativeTime(ts: number, now: number = Date.now()): string {
  const diff = now - ts;
  if (!Number.isFinite(diff) || diff < 0) return "";
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Initials for the recent-card monogram. Falls back to the first letter of
 *  the file name so a card is never blank. */
export function initialsFor(label: string): string {
  const words = (label || "")
    .replace(/[_\-.]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Pull a display name + town off a saved snapshot. Recents predate the
 *  client-field expansion, so every field here is optional by design. */
function describeRecent(rf: HomeRecentFile): { title: string; sub: string } {
  const snap = rf?.json || {};
  const last = String(snap.clientLastName || "").trim();
  const first = String(snap.clientFirstName || "").trim();
  const town = String(snap.clientTown || "").trim();
  const named = [first, last].filter(Boolean).join(" ");
  const title = named || String(rf.name || "").trim() || "Estimate";
  return { title, sub: town };
}

const Icon = {
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  open: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  proposal: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  contract: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 15h6" />
    </svg>
  ),
  visuals: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  pricing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  analytics: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.36.43.64.79.78" />
    </svg>
  ),
};

export default function HomePage({
  userName,
  orgName,
  recents,
  currentClientName,
  currentTown,
  hasWorkInProgress,
  onResume,
  onNewEstimate,
  onOpenFile,
  onOpenRecent,
  onNavigate,
  canEditPricing,
}: HomePageProps) {
  // Frozen at mount rather than ticking: the greeting flipping from afternoon
  // to evening under the user mid-session would be a distraction, not a
  // feature, and a live clock here would re-render the whole page every minute.
  const now = useMemo(() => new Date(), []);
  const firstName = String(userName || "").trim().split(/\s+/)[0] || "";
  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const shownRecents = (recents || []).slice(0, 8);

  const resumeTitle =
    String(currentClientName || "").trim() || "Untitled estimate";
  const resumeSub = String(currentTown || "").trim();

  const jumpTargets: {
    key: HomeNavTarget;
    label: string;
    icon: React.ReactNode;
  }[] = [
    { key: "proposals", label: "Proposal", icon: Icon.proposal },
    { key: "contract", label: "Contract", icon: Icon.contract },
    { key: "visualLibrary", label: "Visual Library", icon: Icon.visuals },
    ...(canEditPricing
      ? [{ key: "pricingAdmin" as HomeNavTarget, label: "Pricing Admin", icon: Icon.pricing }]
      : []),
    { key: "analytics", label: "Analytics", icon: Icon.analytics },
    { key: "settings", label: "Settings", icon: Icon.settings },
  ];

  return (
    <section className="home-pane">
      <header className="home-hero">
        <div className="home-hero__text">
          <h1 className="home-greeting">
            {greetingFor(now)}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="home-subgreeting">
            {orgName ? `${orgName} · ` : ""}
            {dateLabel}
          </p>
        </div>
      </header>

      <div className="home-actions">
        {hasWorkInProgress && (
          <button type="button" className="home-card home-card--resume" onClick={onResume}>
            <span className="home-card__eyebrow">Pick up where you left off</span>
            <span className="home-card__title">{resumeTitle}</span>
            {resumeSub && <span className="home-card__sub">{resumeSub}</span>}
            <span className="home-card__cta">
              Continue <span className="home-card__cta-icon">{Icon.arrow}</span>
            </span>
          </button>
        )}

        <button type="button" className="home-card home-card--new" onClick={onNewEstimate}>
          <span className="home-card__icon">{Icon.plus}</span>
          <span className="home-card__title">New estimate</span>
          <span className="home-card__sub">Start a fresh deck</span>
        </button>

        <button type="button" className="home-card home-card--open" onClick={onOpenFile}>
          <span className="home-card__icon">{Icon.open}</span>
          <span className="home-card__title">Open estimate</span>
          <span className="home-card__sub">Load a saved .est file</span>
        </button>
      </div>

      <section className="home-section">
        <h2 className="home-section__title">Recent estimates</h2>
        {shownRecents.length === 0 ? (
          <div className="home-empty">
            Nothing here yet. Estimates you save or open will show up on this
            screen so you can get back to them in one click.
          </div>
        ) : (
          <div className="home-recents">
            {shownRecents.map((rf) => {
              const { title, sub } = describeRecent(rf);
              return (
                <button
                  key={`${rf.ts}-${rf.name}`}
                  type="button"
                  className="home-recent"
                  onClick={() => onOpenRecent(rf)}
                  title={`${rf.name} — ${new Date(rf.ts).toLocaleString()}`}
                >
                  <span className="home-recent__mono" aria-hidden="true">
                    {initialsFor(title)}
                  </span>
                  <span className="home-recent__body">
                    <span className="home-recent__title">{title}</span>
                    {sub && <span className="home-recent__sub">{sub}</span>}
                  </span>
                  <span className="home-recent__time">{relativeTime(rf.ts, now.getTime())}</span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="home-section">
        <h2 className="home-section__title">Jump to</h2>
        <div className="home-jump">
          {jumpTargets.map((t) => (
            <button
              key={t.key}
              type="button"
              className="home-jump__tile"
              onClick={() => onNavigate(t.key)}
            >
              <span className="home-jump__icon">{t.icon}</span>
              <span className="home-jump__label">{t.label}</span>
            </button>
          ))}
        </div>
      </section>
    </section>
  );
}
